<template>
  <SecondDesktopTabs
    :class="checkScreenDevice(['desktop']) ? 'height-percent-100' : ''"
    :tabs="competencyTabs"
    :isDesktopTabs="true"
    :isMobileTabsModal="true"
    @getCurrentTab="setCurrentTab"
  >
    <template slot="extra-options">
      <div
        class="border--solid border--complementary-50 border--bottom margin-bottom-16"
      >
        <AvatarGroup
          class="margin-bottom-12"
          :data="candidateData"
          :hasProfilePopup="false"
        />
        <AvatarGroup
          class="margin-bottom-12"
          :data="assessorData"
          :hasProfilePopup="false"
        />
      </div>
    </template>

    <template slot="right-icon-item" slot-scope="{ item }">
      <span
        class="color-complementary-50 margin-left-8"
        :class="{
          'color-success': item.hasScore && !item.evidenceErrorCount,
        }"
        v-b-tooltip.hover
        :title="$lang.competencyCheckTooltip"
      >
        <mdicon :name="pathOr('check-circle', ['icon'], item)" size="24" />
      </span>
    </template>
    <template slot="left-icon-item" slot-scope="{ item }">
      <div class="flex-end">
        <ErrorCount
          class="margin-left-8"
          :visible="!!item.evidenceErrorCount"
          :count="item.evidenceErrorCount"
        />
        <DropdownModal
          v-if="item.description"
          :modalWidth="372"
          modalClass="right-0 top-26 box-shadow--modal"
        >
          <template #click-area>
            <span class="color-complementary-200 cursor--pointer">
              <mdicon name="help-circle-outline" size="24" />
            </span>
          </template>
          <template #content>
            <div>
              <div
                class="font-size-16 font-weight-500 color-complementary-400 margin-bottom-4"
              >
                {{ $lang.definitionCompetency }}
              </div>
              <div class="font-size-14 color-complementary-300 text-justify">
                {{ item.description }}
              </div>
            </div>
          </template>
        </DropdownModal>
      </div>
    </template>
    <template :slot="`tab-${currentTabIndex}`">
      <Alert
        v-if="!hasSample"
        :text="$lang.notShowSampleMessage"
        :hasClose="false"
        variant="light-warning"
        class="margin-bottom-16"
      />
      <div>
        <div class="flex-end margin-bottom-16">
          <span
            class="background-surface color-complementary-400 border--solid border--complementary-50 border-radius-8 font-size-14 font-weight-500 flex padding-y-8 padding-x-12 cursor--pointer disable-select width--max-content mobile-width-percent-100 mobile-flex-justify-content-center tablet-width-percent-100 tablet-flex-justify-content-center"
            @click.stop="handleChangeCollapseGroupVisible"
          >
            <mdicon
              :name="
                collapseGroupVisible
                  ? 'unfold-less-horizontal'
                  : 'unfold-more-horizontal'
              "
              size="20"
            />
            {{
              collapseGroupVisible ? $lang.closeAllCards : $lang.openAllCards
            }}
          </span>
        </div>
        <IndicatorCard
          v-for="item in indicators"
          :key="item.uniqueId"
          :item="item"
          :class="{ 'is-skeleton': indicatorsLoading }"
          :indicatorsLoading="indicatorsLoading"
        />
        <div :class="{ 'is-skeleton': indicatorsLoading }">
          <ScoreForm v-if="caseCompetency" :data="caseCompetency" />
        </div>
      </div>
    </template>
  </SecondDesktopTabs>
</template>

<script>
import {
  SecondDesktopTabs,
  AvatarGroup,
  DropdownModal,
  Alert,
  ErrorCount,
} from "@/components/re-design";
import IndicatorCard from "./components/indicator/IndicatorCard.vue";
import ScoreForm from "./components/competency/ScoreForm.vue";
import { computed, inject, onMounted, provide, ref, watch } from "vue";
import { pathOr } from "ramda";
import {
  checkScreenDevice,
  generateCurrentRoute,
  generateError,
} from "@/shared/utils";
import {
  getCenterExecuteCaseCompetencies,
  getCenterExecuteCaseCompetenciesIndicators,
} from "@/modules/kanoon/services";
export default {
  components: {
    SecondDesktopTabs,
    AvatarGroup,
    DropdownModal,
    IndicatorCard,
    ScoreForm,
    Alert,
    ErrorCount,
  },
  setup() {
    // get provided data
    const centerExecuteId = inject("CENTER_EXECUTE_ID");
    const caseId = inject("CASE_ID");
    const candidateData = inject("CANDIDATE_DATA");
    const assessorData = inject("ASSESSOR_DATA");
    const hasSample = inject("HAS_SAMPLE");

    // handle current tab data
    const competencyTabs = ref([]);
    const currentTab = ref(null);
    const currentTabIndex = ref(0);

    // get indicators
    const indicatorsLoading = ref(false);
    const indicators = ref([]);
    const caseCompetency = ref(null);
    const getIndicators = async () => {
      try {
        indicatorsLoading.value = true;
        const competencyId = pathOr(null, ["id"], currentTab.value);
        const response = await getCenterExecuteCaseCompetenciesIndicators(
          centerExecuteId.value,
          caseId.value,
          competencyId
        );
        const { indicatorsData, caseCompetencyData } = response;
        indicators.value = indicatorsData;
        caseCompetency.value = caseCompetencyData;
      } catch (error) {
        generateError(error);
      } finally {
        indicatorsLoading.value = false;
      }
    };

    // set current tab
    const setCurrentTab = (item) => {
      if (!item) return;
      // return when check repeat key is true and currentTab is equal to item
      if (pathOr(null, ["id"], currentTab.value) === item.id) return;

      // set current tab data
      currentTab.value = item;
      currentTabIndex.value = competencyTabs.value.findIndex(
        ({ id }) => id === item.id
      );

      getIndicators();
    };

    // get competencies
    const getCompetenciesData = async (hasSetCurrentTab = true) => {
      try {
        competencyTabs.value = [];
        const caseId = generateCurrentRoute(["params", "caseId"]);
        const response = await getCenterExecuteCaseCompetencies(caseId);
        const { items } = response;
        competencyTabs.value = items.map((item) => item);
        if (hasSetCurrentTab) {
          setCurrentTab(items[0]);
        }
      } catch (error) {
        generateError(error);
      }
    };
    onMounted(() => getCompetenciesData());

    // handle has check evidence in any indicators
    const hasCheckedEvidenceItemInAnyIndicators = ref(false);
    watch(indicators, (data) => {
      hasCheckedEvidenceItemInAnyIndicators.value = data.some((item) => {
        const evidences = pathOr([], ["evidences"], item);
        return evidences.some(({ isSelect }) => isSelect);
      });
    });

    // handle visible group collapses
    const collapseGroupVisible = ref(true);
    const handleChangeCollapseGroupVisible = () => {
      collapseGroupVisible.value = !collapseGroupVisible.value;
      indicators.value = indicators.value.map((indicator) => {
        return {
          ...indicator,
          visibleCollapse: collapseGroupVisible,
        };
      });
    };

    provide(
      "CURRENT_TAB",
      computed(() => currentTab.value)
    );
    provide(
      "COMPETENCY_ID",
      computed(() => caseCompetency.value.competencyId)
    );
    provide("GET_INDICATORS", getIndicators);
    provide("GET_COMPETENCIES_DATA", getCompetenciesData);

    provide(
      "HAS_CHECKED_EVIDENCE_ITEM_IN_ANY_INDICATORS",
      computed(() => hasCheckedEvidenceItemInAnyIndicators.value)
    );

    // return data to be used in template
    return {
      competencyTabs,
      currentTab,
      currentTabIndex,
      setCurrentTab,
      candidateData,
      assessorData,
      pathOr,
      checkScreenDevice,
      indicatorsLoading,
      indicators,
      caseCompetency,
      hasSample,
      collapseGroupVisible,
      handleChangeCollapseGroupVisible,
    };
  },
};
</script>
